import React, {
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
} from 'react';

import clinicalNoteService from './clinicalNoteService.js';
import clinicalTemplateAdminService from './clinicalTemplateAdminService.js';
import ClinicalNoteExpandableField from './ClinicalNoteExpandableField.jsx';

/**
 * Delay before an edit is automatically saved.
 *
 * This matches the rest of the clinical-note editor: the therapist may
 * continue typing normally without creating a request for every keystroke.
 */
const AUTOSAVE_DELAY_MS = 900;

/**
 * Empty editable form used before the backend response is loaded.
 */
const EMPTY_FORM = {
  subjectiveHistory: '',
  aggravatingFactors: '',
  medicalHistory: '',
  surgicalHistory: '',
  mentalCognitiveStatus: '',
  romStrengthComments: '',
  frequencyMinPerWeek: '',
  frequencyMaxPerWeek: '',
  durationWeeks: '',
  rehabPotential: '',
  contraindicationsToTherapy: '',
  plan: '',
};

/**
 * Convert a nullable backend value into a controlled input value.
 */
function toInputValue(value) {
  if (
    value === null ||
    value === undefined
  ) {
    return '';
  }

  return String(value);
}

/**
 * Convert optional text into the String/null representation expected by
 * the Spring Boot request DTO.
 */
function toNullableText(value) {
  if (
    value === null ||
    value === undefined
  ) {
    return null;
  }

  const normalized =
    String(value).trim();

  return normalized || null;
}

/**
 * Convert an optional numeric draft field into Integer/null.
 *
 * Blank values intentionally remain null because Evaluation History may be
 * incomplete while the note remains in DRAFT.
 */
function toNullableInteger(value) {
  if (
    value === null ||
    value === undefined ||
    String(value).trim() === ''
  ) {
    return null;
  }

  const numericValue =
    Number(value);

  if (
    !Number.isInteger(
      numericValue
    )
  ) {
    return null;
  }

  return numericValue;
}

/**
 * Resolve the parent ClinicalNoteMain optimistic-locking version.
 */
function resolveEntityVersion(response) {
  if (!response) {
    return null;
  }

  return (
    response.entityVersion ??
    response.note?.entityVersion ??
    response.clinicalNote?.entityVersion ??
    null
  );
}

/**
 * Map the permanent Evaluation History response into controlled form state.
 *
 * The frontend does not invent default clinical wording. Any defaults shown
 * here must have been returned by the backend for this note.
 */
function responseToForm(response) {
  return {
    subjectiveHistory:
      response?.subjectiveHistory ?? '',

    aggravatingFactors:
      response?.aggravatingFactors ?? '',

    medicalHistory:
      response?.medicalHistory ?? '',

    surgicalHistory:
      response?.surgicalHistory ?? '',

    mentalCognitiveStatus:
      response?.mentalCognitiveStatus ?? '',

    romStrengthComments:
      response?.romStrengthComments ?? '',

    frequencyMinPerWeek:
      toInputValue(
        response?.frequencyMinPerWeek
      ),

    frequencyMaxPerWeek:
      toInputValue(
        response?.frequencyMaxPerWeek
      ),

    durationWeeks:
      toInputValue(
        response?.durationWeeks
      ),

    rehabPotential:
      response?.rehabPotential ?? '',

    contraindicationsToTherapy:
      response?.contraindicationsToTherapy ?? '',

    plan:
      response?.plan ?? '',
  };
}

/**
 * Browser-side validation for the structured Frequency / Duration fields.
 *
 * Draft fields remain independently optional. The backend remains the source
 * of truth for final clinical-note completeness before review/signing.
 */
function validateEvaluationHistoryForm(form) {
  const errors = {};

  const numericFields = [
    [
      'frequencyMinPerWeek',
      form.frequencyMinPerWeek,
      'Minimum frequency',
    ],
    [
      'frequencyMaxPerWeek',
      form.frequencyMaxPerWeek,
      'Maximum frequency',
    ],
    [
      'durationWeeks',
      form.durationWeeks,
      'Duration',
    ],
  ];

  numericFields.forEach(
    ([field, value, label]) => {
      if (
        value === null ||
        value === undefined ||
        String(value).trim() === ''
      ) {
        return;
      }

      const numericValue =
        Number(value);

      if (
        !Number.isInteger(
          numericValue
        )
      ) {
        errors[field] =
          `${label} must be a whole number.`;
        return;
      }

      if (numericValue <= 0) {
        errors[field] =
          `${label} must be greater than zero.`;
      }
    }
  );

  const frequencyMin =
    toNullableInteger(
      form.frequencyMinPerWeek
    );

  const frequencyMax =
    toNullableInteger(
      form.frequencyMaxPerWeek
    );

  if (
    frequencyMin !== null &&
    frequencyMax !== null &&
    frequencyMax < frequencyMin
  ) {
    errors.frequencyMaxPerWeek =
      'Maximum frequency cannot be less than minimum frequency.';
  }

  return errors;
}

/**
 * Shared multiline clinical-history field.
 */
function TextAreaField({
  id,
  label,
  value,
  rows = 4,
  disabled,
  placeholder,
  onChange,
  initiallyCollapsed = false,
}) {
  return (
    <ClinicalNoteExpandableField
      id={id}
      label={label}
      value={value}
      rows={rows}
      disabled={disabled}
      placeholder={placeholder}
      onChange={onChange}
      initiallyCollapsed={initiallyCollapsed}
    />
  );
}

/**
 * Shared optional positive whole-number field.
 */
function NumberField({
  id,
  label,
  value,
  error,
  disabled,
  suffix,
  onChange,
}) {
  return (
    <div
      style={{
        flex: '1 1 180px',
        minWidth: 170,
      }}
    >
      <label
        htmlFor={id}
        style={{
          display: 'block',
          fontWeight: 700,
          fontSize: 14,
          marginBottom: 6,
          color: '#222',
        }}
      >
        {label}
      </label>

      <div
        style={{
          display: 'flex',
          alignItems: 'center',
          gap: 8,
        }}
      >
        <input
          id={id}
          type="number"
          min="1"
          step="1"
          value={value}
          disabled={disabled}
          onChange={(event) =>
            onChange(
              event.target.value
            )
          }
          aria-invalid={
            Boolean(error)
          }
          aria-describedby={
            error
              ? `${id}-error`
              : undefined
          }
          style={{
            width: '100%',
            boxSizing: 'border-box',
            minHeight: 42,
            border:
              error
                ? '1px solid #b42318'
                : '1px solid #bbb',
            borderRadius: 6,
            padding: '8px 10px',
            fontSize: 16,
            background:
              disabled
                ? '#f3f4f6'
                : '#fff',
          }}
        />

        {suffix && (
          <span
            style={{
              flex: '0 0 auto',
              fontSize: 13,
              color: '#555',
              whiteSpace: 'nowrap',
            }}
          >
            {suffix}
          </span>
        )}
      </div>

      {error && (
        <div
          id={`${id}-error`}
          role="alert"
          style={{
            marginTop: 5,
            fontSize: 12,
            color: '#b42318',
          }}
        >
          {error}
        </div>
      )}
    </div>
  );
}


/**
 * Split the permanent snapshot text into selections for the picker.
 *
 * The database intentionally keeps one human-readable snapshot string so a
 * signed note is not affected by later Admin template edits.
 */
function parseAggravatingFactorText(value) {
  return String(value || '')
    .split(',')
    .map((item) => item.trim())
    .filter(Boolean);
}

/** Searchable multi-select picker with support for patient-specific custom text. */
function AggravatingFactorPicker({
  value,
  options,
  loading,
  loadError,
  disabled,
  onChange,
}) {
  const [open, setOpen] = useState(false);
  const [search, setSearch] = useState('');
  const [customValue, setCustomValue] = useState('');
  const [customExpanded, setCustomExpanded] = useState(false);
  const [showSelected, setShowSelected] = useState(false);

  const selected = useMemo(
    () => parseAggravatingFactorText(value),
    [value]
  );

  const selectedSet = useMemo(
    () => new Set(selected.map((item) => item.toLowerCase())),
    [selected]
  );

  const visibleOptions = useMemo(() => {
    const normalizedSearch = search.trim().toLowerCase();
    return (Array.isArray(options) ? options : [])
      .filter((item) => {
        if (!normalizedSearch) {
          return true;
        }
        return [item?.displayName, item?.factorCode]
          .filter(Boolean)
          .join(' ')
          .toLowerCase()
          .includes(normalizedSearch);
      });
  }, [options, search]);

  const commit = (items) => {
    const unique = [];
    const seen = new Set();

    items.forEach((item) => {
      const normalized = String(item || '').trim();
      const key = normalized.toLowerCase();
      if (!normalized || seen.has(key)) {
        return;
      }
      seen.add(key);
      unique.push(normalized);
    });

    onChange(unique.join(', '));
  };

  const toggleOption = (displayName) => {
    const normalized = String(displayName || '').trim();
    const key = normalized.toLowerCase();

    if (selectedSet.has(key)) {
      commit(selected.filter((item) => item.toLowerCase() !== key));
    } else {
      commit([...selected, normalized]);
    }
  };

  const removeItem = (displayName) => {
    const key = String(displayName || '').trim().toLowerCase();
    commit(selected.filter((item) => item.toLowerCase() !== key));
  };

  const addCustom = () => {
    // The saved aggravating-factor snapshot is comma-delimited. Preserve
    // one custom entry by replacing delimiters with semicolons.
    const normalized = customValue.trim().replace(/\s*,\s*/g, '; ').replace(/\s*\n\s*/g, ' ');
    if (!normalized) {
      return;
    }
    commit([...selected, normalized]);
    setCustomValue('');
  };

  return (
    <div>
      <label
        style={{
          display: 'block',
          fontWeight: 700,
          fontSize: 14,
          marginBottom: 6,
          color: '#222',
        }}
      >
        Aggravating Factors
      </label>

      <button
        type="button"
        disabled={disabled}
        onClick={() => setOpen((current) => !current)}
        style={{
          width: '100%',
          minHeight: 42,
          textAlign: 'left',
          border: '1px solid #bbb',
          borderRadius: 6,
          padding: '9px 10px',
          background: disabled ? '#f3f4f6' : '#fff',
          color: '#222',
          cursor: disabled ? 'not-allowed' : 'pointer',
        }}
      >
        {selected.length > 0
          ? `${selected.length} selected — click to ${open ? 'close' : 'edit'}`
          : 'Select aggravating factors...'}
      </button>

      {selected.length > 0 && (
        <div style={{ display: 'flex', flexWrap: 'wrap', gap: 6, marginTop: 8 }}>
          {selected.map((item) => (
            <span
              key={item}
              style={{
                display: 'inline-flex',
                alignItems: 'center',
                gap: 5,
                border: '1px solid #cbd5e1',
                borderRadius: 999,
                padding: '4px 8px',
                background: '#f8fafc',
                fontSize: 12,
              }}
            >
              {item}
              {!disabled && (
                <button
                  type="button"
                  onClick={() => removeItem(item)}
                  aria-label={`Remove ${item}`}
                  style={{ border: 0, background: 'transparent', cursor: 'pointer', padding: 0 }}
                >
                  ×
                </button>
              )}
            </span>
          ))}
        </div>
      )}

      {open && !disabled && (
        <div
          style={{
            marginTop: 8,
            border: '1px solid #cbd5e1',
            borderRadius: 8,
            padding: 12,
            background: '#fff',
          }}
        >
          <input
            type="search"
            value={search}
            onChange={(event) => setSearch(event.target.value)}
            placeholder="Search aggravating factors..."
            style={{
              width: '100%',
              boxSizing: 'border-box',
              minHeight: 40,
              border: '1px solid #bbb',
              borderRadius: 6,
              padding: '8px 10px',
              marginBottom: 10,
            }}
          />

          {loadError && (
            <div style={{ marginBottom: 8, fontSize: 12, color: '#9a6700' }}>
              {loadError} Custom factors can still be added.
            </div>
          )}

          <div style={{ display: 'flex', justifyContent: 'flex-end', marginBottom: 6 }}>
            <button
              type="button"
              onClick={() => setShowSelected((current) => !current)}
              style={{ fontSize: 12, padding: '4px 8px', border: '1px solid #cbd5e1', borderRadius: 6, background: '#fff' }}
            >
              {showSelected ? 'Compact choices' : 'Show more choices'}
            </button>
          </div>
          {loading ? (
            <div style={{ fontSize: 13, color: '#666' }}>Loading options...</div>
          ) : visibleOptions.length === 0 ? (
            <div style={{ fontSize: 13, color: '#666' }}>No configured options match this search.</div>
          ) : (
            <div
              style={{
                display: 'grid',
                gridTemplateColumns: 'repeat(auto-fit, minmax(220px, 1fr))',
                gap: 6,
                maxHeight: showSelected ? 520 : 240,
                overflowY: 'auto',
              }}
            >
              {visibleOptions.map((option) => {
                const name = option?.displayName || '';
                const checked = selectedSet.has(name.toLowerCase());
                return (
                  <label
                    key={option.aggravatingFactorTemplateId ?? option.factorCode}
                    style={{
                      display: 'flex',
                      alignItems: 'flex-start',
                      gap: 8,
                      border: '1px solid #e2e8f0',
                      borderRadius: 6,
                      padding: 8,
                      cursor: 'pointer',
                    }}
                  >
                    <input
                      type="checkbox"
                      checked={checked}
                      onChange={() => toggleOption(name)}
                    />
                    <span>
                      <strong style={{ display: 'block', fontSize: 13 }}>{name}</strong>
                      <small style={{ color: '#667085' }}>
                        {option.bodyPartTemplateId ? 'Body-part specific' : 'Universal'}
                      </small>
                    </span>
                  </label>
                );
              })}
            </div>
          )}

          <div
            style={{
              display: 'grid',
              gap: 8,
              minWidth: 0,
              marginTop: 12,
              paddingTop: 12,
              borderTop: '1px solid #e2e8f0',
            }}
          >
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', gap: 8, flexWrap: 'wrap' }}>
              <label htmlFor="clinical-note-custom-aggravating-factor" style={{ fontWeight: 700, fontSize: 13 }}>
                Custom aggravating factor
              </label>
              <button
                type="button"
                onClick={() => setCustomExpanded((current) => !current)}
                aria-pressed={customExpanded}
                style={{ padding: '5px 10px', borderRadius: 6, border: '1px solid #cbd5e1', background: '#fff' }}
              >
                {customExpanded ? 'Normal size' : 'Expand editor'}
              </button>
            </div>
            <textarea
              id="clinical-note-custom-aggravating-factor"
              value={customValue}
              onChange={(event) => setCustomValue(event.target.value)}
              onKeyDown={(event) => {
                if (event.key === 'Enter' && (event.ctrlKey || event.metaKey)) {
                  event.preventDefault();
                  addCustom();
                }
              }}
              placeholder="Describe other activities or positions that aggravate symptoms..."
              rows={customExpanded ? 10 : 4}
              maxLength={1000}
              style={{
                width: '100%',
                minWidth: 0,
                boxSizing: 'border-box',
                minHeight: customExpanded ? 250 : 110,
                maxHeight: '70vh',
                resize: 'vertical',
                overflowY: 'auto',
                border: '1px solid #bbb',
                borderRadius: 8,
                padding: '10px 12px',
                fontSize: 14,
                lineHeight: 1.5,
              }}
            />
            <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 10, flexWrap: 'wrap' }}>
              <small style={{ color: '#667085' }}>Ctrl+Enter adds the factor. Use the bottom-right corner to resize.</small>
              <button type="button" onClick={addCustom} disabled={!customValue.trim()}>
                Add Custom
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

/**
 * Permanent built-in history section used by:
 *
 * - Initial Evaluation
 * - Re-Evaluation
 *
 * This data is not template-driven patient documentation. The values returned
 * by the backend are loaded directly and become patient-specific once saved.
 *
 * The component follows the same autosave/version pattern as the other
 * clinical-note sections:
 *
 * - load section + parent note version
 * - allow incomplete DRAFT data
 * - debounce edits
 * - use the shared service mutation queue
 * - refresh the parent ClinicalNoteMain version after every successful save
 * - never silently overwrite an optimistic-lock conflict
 */
export default function ClinicalNoteEvaluationHistorySection({
  clinicalNoteId,
  disabled = false,
  onSaved,
  onVersionChange,
  navigationCoordinator,
}) {
  const [
    form,
    setForm,
  ] = useState({
    ...EMPTY_FORM,
  });

  const [
    historyMetadata,
    setHistoryMetadata,
  ] = useState(null);

  const [
    loading,
    setLoading,
  ] = useState(true);

  const [
    loadError,
    setLoadError,
  ] = useState('');

  const [
    saveError,
    setSaveError,
  ] = useState('');

  const [
    saveStatus,
    setSaveStatus,
  ] = useState('saved');

  const [
    editRevision,
    setEditRevision,
  ] = useState(0);

  const [
    initialized,
    setInitialized,
  ] = useState(false);

  const [aggravatingOptions, setAggravatingOptions] = useState([]);
  const [aggravatingOptionsLoading, setAggravatingOptionsLoading] = useState(false);
  const [aggravatingOptionsError, setAggravatingOptionsError] = useState('');

  const formRef =
    useRef(form);

  const versionRef =
    useRef(null);

  const revisionRef =
    useRef(0);

  const savedRevisionRef =
    useRef(0);

  const savingRef =
    useRef(false);

  const mountedRef =
    useRef(true);

  const flushSaveRef = useRef(null);
  const inFlightSaveRef = useRef(null);
  const failedRevisionRef = useRef(null);
  const initializedRef = useRef(false);
  const loadErrorRef = useRef('');
  const activeNoteIdRef = useRef(String(clinicalNoteId ?? ''));


  useEffect(() => {
    formRef.current =
      form;
  }, [form]);

  useEffect(() => {
    mountedRef.current =
      true;

    return () => {
      mountedRef.current =
        false;
    };
  }, []);

  /**
   * Load the permanent IE/Re-Evaluation history and the current parent note
   * version whenever the note ID changes.
   */
  useEffect(() => {
    let cancelled = false;

    async function loadEvaluationHistory() {
      setLoading(true);
      setInitialized(false);
      initializedRef.current = false;
      activeNoteIdRef.current = String(clinicalNoteId ?? '');
      loadErrorRef.current = '';
      setLoadError('');
      setSaveError('');
      setSaveStatus('saved');
      setAggravatingOptions([]);
      setAggravatingOptionsError('');

      revisionRef.current = 0;
      savedRevisionRef.current = 0;
      failedRevisionRef.current = null;

      try {
        const [
          historyResponse,
          noteResponse,
        ] = await Promise.all([
          clinicalNoteService
            .getEvaluationHistory(
              clinicalNoteId
            ),

          clinicalNoteService
            .getClinicalNote(
              clinicalNoteId
            ),
        ]);

        if (cancelled) {
          return;
        }

        if (
          !historyResponse ||
          typeof historyResponse !==
            'object'
        ) {
          throw new Error(
            'Evaluation history could not be loaded.'
          );
        }

        const version =
          resolveEntityVersion(
            noteResponse
          );

        if (
          version === null ||
          version === undefined
        ) {
          throw new Error(
            'The clinical note version could not be loaded.'
          );
        }

        versionRef.current =
          version;

        const bodyPartTemplateId =
          Number(
            noteResponse?.bodyPartTemplateId ??
            noteResponse?.note?.bodyPartTemplateId ??
            noteResponse?.clinicalNote?.bodyPartTemplateId ??
            0
          );

        setAggravatingOptionsLoading(true);
        setAggravatingOptionsError('');

        try {
          const options =
            await clinicalTemplateAdminService
              .getAvailableAggravatingFactorTemplates(
                Number.isInteger(bodyPartTemplateId) && bodyPartTemplateId > 0
                  ? bodyPartTemplateId
                  : null
              );

          if (!cancelled) {
            setAggravatingOptions(Array.isArray(options) ? options : []);
          }
        } catch (optionError) {
          if (!cancelled) {
            setAggravatingOptions([]);
            setAggravatingOptionsError(
              optionError?.message || 'Configured aggravating factors could not be loaded.'
            );
          }
        } finally {
          if (!cancelled) {
            setAggravatingOptionsLoading(false);
          }
        }

        const loadedForm =
          responseToForm(
            historyResponse
          );

        formRef.current =
          loadedForm;

        setForm(
          loadedForm
        );

        setHistoryMetadata(
          historyResponse
        );

        initializedRef.current = true;
        setInitialized(true);
      } catch (error) {
        if (cancelled) {
          return;
        }

        loadErrorRef.current = error?.message || 'Evaluation history could not be loaded.';
        setLoadError(loadErrorRef.current);
      } finally {
        if (!cancelled) {
          setLoading(false);
        }
      }
    }

    if (clinicalNoteId) {
      loadEvaluationHistory();
    } else {
      setLoadError(
        'Clinical note ID is required.'
      );
      setLoading(false);
    }

    return () => {
      cancelled = true;
    };
  }, [clinicalNoteId]);

  const validationErrors =
    useMemo(
      () =>
        validateEvaluationHistoryForm(
          form
        ),
      [form]
    );

  const hasValidationErrors =
    Object.keys(
      validationErrors
    ).length > 0;

  /**
   * Mark one field as changed and allow the debounce timer to perform the
   * actual save.
   */
  function updateField(
    field,
    value
  ) {
    if (disabled) {
      return;
    }

    setForm((current) => {
      const next = {
        ...current,
        [field]: value,
      };

      formRef.current =
        next;

      return next;
    });

    revisionRef.current += 1;

    setEditRevision(
      revisionRef.current
    );

    failedRevisionRef.current = null;
    setSaveError('');
    setSaveStatus('unsaved');
  }

  /**
   * Refresh ClinicalNoteMain after a section save so all later section saves
   * continue from the newest optimistic-locking version.
   */
  const refreshEntityVersion =
    useCallback(
      async () => {
        const noteResponse =
          await clinicalNoteService
            .getClinicalNote(
              clinicalNoteId
            );

        const newVersion =
          resolveEntityVersion(
            noteResponse
          );

        if (
          newVersion === null ||
          newVersion === undefined
        ) {
          throw new Error(
            'Evaluation history was saved, but the updated clinical note version could not be loaded.'
          );
        }

        versionRef.current =
          newVersion;

        if (
          typeof onVersionChange ===
          'function'
        ) {
          onVersionChange(
            newVersion,
            noteResponse
          );
        }

        return noteResponse;
      },
      [
        clinicalNoteId,
        onVersionChange,
      ]
    );

  /**
   * Flush all edited revisions, including changes made during an active
   * request. The returned promise is a barrier for visit navigation.
   * Backend conflicts are never silently overwritten or retried forever.
   */
  const flushSave = useCallback(async (forceRetry = false) => {
    // Await a save already running instead of returning immediately.
    if (inFlightSaveRef.current) {
      await inFlightSaveRef.current;
    }
    if (disabled) return;
    if (!initializedRef.current) {
      throw new Error('Evaluation History is still loading.');
    }
    if (loadErrorRef.current) throw new Error(loadErrorRef.current);

    const run = async () => {
      while (revisionRef.current > savedRevisionRef.current) {
        const revisionBeingSaved = revisionRef.current;
        if (!forceRetry && failedRevisionRef.current === revisionBeingSaved) {
          throw new Error('Evaluation History autosave failed. Use Retry Save or correct the values.');
        }
        forceRetry = false;
        const currentForm = { ...formRef.current };
        const errors = validateEvaluationHistoryForm(currentForm);
        if (Object.keys(errors).length > 0) {
          setSaveStatus('invalid');
          throw new Error('Correct the invalid Evaluation History values before changing visits.');
        }

        savingRef.current = true;
        setSaveStatus('saving');
        setSaveError('');
        try {
          if (versionRef.current === null || versionRef.current === undefined) {
            await refreshEntityVersion();
          }
          const response = await clinicalNoteService.saveEvaluationHistory(clinicalNoteId, {
            expectedVersion: versionRef.current,
            subjectiveHistory: toNullableText(currentForm.subjectiveHistory),
            aggravatingFactors: toNullableText(currentForm.aggravatingFactors),
            medicalHistory: toNullableText(currentForm.medicalHistory),
            surgicalHistory: toNullableText(currentForm.surgicalHistory),
            mentalCognitiveStatus: toNullableText(currentForm.mentalCognitiveStatus),
            romStrengthComments: currentForm.romStrengthComments.trim(),
            frequencyMinPerWeek: toNullableInteger(currentForm.frequencyMinPerWeek),
            frequencyMaxPerWeek: toNullableInteger(currentForm.frequencyMaxPerWeek),
            durationWeeks: toNullableInteger(currentForm.durationWeeks),
            rehabPotential: toNullableText(currentForm.rehabPotential),
            contraindicationsToTherapy: toNullableText(currentForm.contraindicationsToTherapy),
            plan: toNullableText(currentForm.plan),
          });
          if (!mountedRef.current || activeNoteIdRef.current !== String(clinicalNoteId)) {
            throw new Error('Evaluation History changed notes during an active save.');
          }
          const noteResponse = await refreshEntityVersion();
          if (!mountedRef.current || activeNoteIdRef.current !== String(clinicalNoteId)) {
            throw new Error('Evaluation History changed notes during an active save.');
          }
          setHistoryMetadata(response);
          // Preserve newer edits typed while the request was in flight.
          if (revisionRef.current === revisionBeingSaved) {
            const savedForm = responseToForm(response);
            formRef.current = savedForm;
            setForm(savedForm);
          }
          savedRevisionRef.current = revisionBeingSaved;
          failedRevisionRef.current = null;
          if (typeof onSaved === 'function') onSaved(response, noteResponse);
          setSaveStatus(revisionRef.current > savedRevisionRef.current ? 'unsaved' : 'saved');
        } catch (error) {
          failedRevisionRef.current = revisionBeingSaved;
          const message = error?.message || 'Evaluation History could not be autosaved.';
          if (mountedRef.current && activeNoteIdRef.current === String(clinicalNoteId)) {
            setSaveStatus('error');
            setSaveError(message);
          }
          throw new Error(message);
        } finally {
          savingRef.current = false;
        }
      }
    };

    const promise = run();
    inFlightSaveRef.current = promise;
    try {
      await promise;
    } finally {
      if (inFlightSaveRef.current === promise) inFlightSaveRef.current = null;
    }
  }, [clinicalNoteId, disabled, onSaved, refreshEntityVersion]);

  useEffect(() => {
    flushSaveRef.current = flushSave;
  }, [flushSave]);

  /** Register with the same coordinator as Pain, ROM, Strength, and Goals. */
  useEffect(() => {
    if (!navigationCoordinator || !clinicalNoteId) return undefined;
    return navigationCoordinator.register(clinicalNoteId, 'evaluation-history', {
      flush: () => flushSaveRef.current?.(),
      getState: () => {
        const dirty = revisionRef.current > savedRevisionRef.current;
        return {
          dirty,
          saving: Boolean(inFlightSaveRef.current),
          invalid: dirty && Object.keys(validateEvaluationHistoryForm(formRef.current)).length > 0,
          error: loadErrorRef.current || (
            dirty && failedRevisionRef.current === revisionRef.current
              ? 'Evaluation History autosave failed. Use Retry Save or edit the field.'
              : ''
          ),
        };
      },
    });
  }, [navigationCoordinator, clinicalNoteId]);

  /** Explicitly permit retry of a failed optimistic-lock/network request. */
  function retrySave() {
    failedRevisionRef.current = null;
    setSaveError('');
    void flushSaveRef.current?.(true).catch(() => {});
  }

  /**
   * Debounced autosave trigger.
   */
  useEffect(() => {
    if (
      !initialized ||
      disabled ||
      editRevision <=
        savedRevisionRef.current
    ) {
      return undefined;
    }

    if (hasValidationErrors) {
      setSaveStatus('invalid');
      return undefined;
    }
    if (failedRevisionRef.current === revisionRef.current) return undefined;

    const timer =
      window.setTimeout(
        () => {
          flushSaveRef
            .current?.().catch(() => {});
        },
        AUTOSAVE_DELAY_MS
      );

    return () => {
      window.clearTimeout(
        timer
      );
    };
  }, [
    editRevision,
    initialized,
    disabled,
    hasValidationErrors,
  ]);

  const saveStatusText =
    useMemo(() => {
      switch (saveStatus) {
        case 'saving':
          return 'Saving…';

        case 'unsaved':
          return 'Unsaved changes';

        case 'invalid':
          return 'Waiting for valid values';

        case 'error':
          return 'Autosave failed';

        default:
          return 'Saved';
      }
    }, [saveStatus]);

  if (loading) {
    return (
      <section
        style={{
          border: '1px solid #ddd',
          borderRadius: 10,
          padding: 16,
          background: '#fff',
        }}
      >
        Loading evaluation history…
      </section>
    );
  }

  if (loadError) {
    return (
      <section
        style={{
          border: '1px solid #c62828',
          borderRadius: 10,
          padding: 16,
          background: '#fff5f5',
        }}
      >
        <strong>
          Evaluation history could not be loaded
        </strong>

        <p
          style={{
            marginBottom: 0,
          }}
        >
          {loadError}
        </p>
      </section>
    );
  }

  return (
    <section
      style={{
        border: '1px solid #d7d7d7',
        borderRadius: 10,
        background: '#fff',
        overflow: 'hidden',
      }}
    >
      {/* ===================================================
          SECTION HEADER
          =================================================== */}
      <div
        style={{
          display: 'flex',
          justifyContent: 'space-between',
          alignItems: 'center',
          gap: 12,
          flexWrap: 'wrap',
          padding: '13px 16px',
          background: '#f7f8fa',
          borderBottom: '1px solid #ddd',
        }}
      >
        <div>
          <h3
            style={{
              margin: 0,
              fontSize: 17,
            }}
          >
            Evaluation History
          </h3>

          <div
            style={{
              marginTop: 3,
              fontSize: 12,
              color: '#666',
            }}
          >
            Permanent Initial Evaluation / Re-Evaluation history
          </div>
        </div>

        <div
          aria-live="polite"
          style={{
            fontSize: 13,
            fontWeight: 700,
            color:
              saveStatus === 'error'
                ? '#b42318'
                : saveStatus === 'invalid'
                  ? '#9a6700'
                  : saveStatus === 'saving'
                    ? '#555'
                    : '#2e6a35',
          }}
        >
          {disabled
            ? 'Read only'
            : saveStatusText}
        </div>
      </div>

      <div
        style={{
          padding: 12,
          display: 'grid',
          gap: 12,
        }}
      >
        {/* =================================================
            PATIENT HISTORY
            ================================================= */}
        <TextAreaField
          id="clinical-note-subjective-history"
          label="Subjective History / Present Condition / Mechanism of Injury"
          value={
            form.subjectiveHistory
          }
          rows={5}
          disabled={disabled}
          placeholder="Enter patient-specific subjective history, present condition, and mechanism of injury."
          onChange={(value) =>
            updateField(
              'subjectiveHistory',
              value
            )
          }
        />

        <AggravatingFactorPicker
          value={form.aggravatingFactors}
          options={aggravatingOptions}
          loading={aggravatingOptionsLoading}
          loadError={aggravatingOptionsError}
          disabled={disabled}
          onChange={(value) =>
            updateField(
              'aggravatingFactors',
              value
            )
          }
        />

        <div
          style={{
            display: 'grid',
            gridTemplateColumns:
              'repeat(auto-fit, minmax(280px, 1fr))',
            gap: 16,
          }}
        >
          <TextAreaField
            id="clinical-note-medical-history"
            label="Medical History"
          initiallyCollapsed
            value={
              form.medicalHistory
            }
            disabled={disabled}
            placeholder="Enter relevant patient medical history."
            onChange={(value) =>
              updateField(
                'medicalHistory',
                value
              )
            }
          />

          <TextAreaField
            id="clinical-note-surgical-history"
            label="Surgical History"
          initiallyCollapsed
            value={
              form.surgicalHistory
            }
            disabled={disabled}
            placeholder="Enter relevant patient surgical history."
            onChange={(value) =>
              updateField(
                'surgicalHistory',
                value
              )
            }
          />
        </div>

        <TextAreaField
          id="clinical-note-mental-cognitive-status"
          label="Mental / Cognitive Status"
          initiallyCollapsed
          value={
            form.mentalCognitiveStatus
          }
          disabled={disabled}
          placeholder="Enter patient-specific mental or cognitive status information when applicable."
          onChange={(value) =>
            updateField(
              'mentalCognitiveStatus',
              value
            )
          }
        />

        {/* =================================================
            ROM / STRENGTH COMMENTS
            Shown under the combined measurements in the signed PDF.
            ================================================= */}
        <TextAreaField
          id="clinical-note-rom-strength-comments"
          label="ROM / Strength Comments"
          value={form.romStrengthComments}
          rows={2}
          initiallyCollapsed
          disabled={disabled}
          placeholder="Enter comments about range of motion and strength."
          onChange={(value) =>
            updateField('romStrengthComments', value)
          }
        />

        {/* =================================================
            FREQUENCY / DURATION
            ================================================= */}
        <div>
          <div
            style={{
              fontWeight: 700,
              fontSize: 14,
              marginBottom: 8,
              color: '#222',
            }}
          >
            Frequency &amp; Duration
          </div>

          <div
            style={{
              display: 'flex',
              gap: 14,
              flexWrap: 'wrap',
            }}
          >
            <NumberField
              id="clinical-note-frequency-min"
              label="Minimum Frequency"
              value={
                form.frequencyMinPerWeek
              }
              error={
                validationErrors
                  .frequencyMinPerWeek
              }
              disabled={disabled}
              suffix="times/week"
              onChange={(value) =>
                updateField(
                  'frequencyMinPerWeek',
                  value
                )
              }
            />

            <NumberField
              id="clinical-note-frequency-max"
              label="Maximum Frequency"
              value={
                form.frequencyMaxPerWeek
              }
              error={
                validationErrors
                  .frequencyMaxPerWeek
              }
              disabled={disabled}
              suffix="times/week"
              onChange={(value) =>
                updateField(
                  'frequencyMaxPerWeek',
                  value
                )
              }
            />

            <NumberField
              id="clinical-note-duration-weeks"
              label="Duration"
              value={
                form.durationWeeks
              }
              error={
                validationErrors
                  .durationWeeks
              }
              disabled={disabled}
              suffix="weeks"
              onChange={(value) =>
                updateField(
                  'durationWeeks',
                  value
                )
              }
            />
          </div>

          <div
            style={{
              marginTop: 8,
              fontSize: 12,
              color: '#666',
            }}
          >
            These values may remain incomplete while the note is a Draft.
            Final completeness is checked by the clinical-note review workflow.
          </div>
        </div>

        {/* =================================================
            PERMANENT PLAN FIELDS
            ================================================= */}
        <TextAreaField
          id="clinical-note-rehab-potential"
          label="Rehab Potential"
          initiallyCollapsed
          value={
            form.rehabPotential
          }
          rows={3}
          disabled={disabled}
          placeholder="Enter the patient-specific rehabilitation potential."
          onChange={(value) =>
            updateField(
              'rehabPotential',
              value
            )
          }
        />

        <TextAreaField
          id="clinical-note-contraindications"
          label="Contraindications to Therapy"
          initiallyCollapsed
          value={
            form.contraindicationsToTherapy
          }
          rows={3}
          disabled={disabled}
          placeholder="Enter patient-specific contraindications to therapy."
          onChange={(value) =>
            updateField(
              'contraindicationsToTherapy',
              value
            )
          }
        />

        <TextAreaField
          id="clinical-note-plan"
          label="Plan"
          initiallyCollapsed
          value={
            form.plan
          }
          rows={5}
          disabled={disabled}
          placeholder="Enter the patient-specific clinical plan."
          onChange={(value) =>
            updateField(
              'plan',
              value
            )
          }
        />

        {/* =================================================
            SAVE ERROR
            ================================================= */}
        {saveError && (
          <div
            role="alert"
            style={{
              border: '1px solid #b42318',
              borderRadius: 8,
              background: '#fff5f5',
              padding: 12,
              color: '#8a1c13',
            }}
          >
            <strong>
              Evaluation history was not saved.
            </strong>

            <div
              style={{
                marginTop: 4,
              }}
            >
              {saveError}
            </div>
            {!disabled && (
              <button type="button" onClick={retrySave} style={{ marginTop: 8 }}>
                Retry Save
              </button>
            )}
          </div>
        )}

        {/* =================================================
            SAVED ROW INFORMATION
            ================================================= */}
        {historyMetadata
          ?.clinicalNoteEvaluationHistoryId && (
          <div
            style={{
              fontSize: 12,
              color: '#777',
            }}
          >
            Evaluation history record #{historyMetadata.clinicalNoteEvaluationHistoryId}
          </div>
        )}

        {/* =================================================
            READ-ONLY MESSAGE
            ================================================= */}
        {disabled && (
          <div
            style={{
              fontSize: 13,
              color: '#666',
            }}
          >
            This permanent evaluation section cannot be edited because the
            clinical note is currently read only or locked.
          </div>
        )}
      </div>
    </section>
  );
}
